/**
 * Generic async key-value store backed by IndexedDB, with a synchronous
 * in-memory read cache and a one-time migration off a legacy `localStorage`
 * key per entry. Generalizes the pattern proven in `draft-store.util.ts` and
 * `published-snapshot.util.ts` (built for the same reason: `localStorage`'s
 * ~5-10MB quota was silently exceeded by blob-heavy data like photo data
 * URLs, causing data to appear saved and then vanish on reload) so the same
 * fix applies to every other `localStorage` call site without re-deriving
 * IDB open/transaction/migration boilerplate per domain.
 *
 * Multiple stores created with the default db/store name share one
 * IndexedDB database (`cnt-landing-kv`) and one open connection — new
 * domains are just new key-string prefixes (e.g. `my-rv:profiles`,
 * `payments:cards`), so adding one never requires a schema/version bump.
 * Pass `dbName`/`storeName` to opt a store out into its own database (used
 * by `draft-store.util.ts`/`published-snapshot.util.ts`, which keep their
 * existing separate databases).
 */

const DEFAULT_DB_NAME = 'cnt-landing-kv';
const DEFAULT_STORE_NAME = 'kv';
const DEFAULT_VERSION = 1;

export interface IdbKvStoreConfig<T> {
  /** Defaults to the shared 'cnt-landing-kv' database. */
  dbName?: string;
  /** Defaults to 'kv'. */
  storeName?: string;
  /** Defaults to 1. */
  version?: number;
  /** idbKey -> legacy localStorage key, migrated once on the first `init()`. */
  legacyKeys: Record<string, string>;
  /** Per-key validation/coercion of the legacy `JSON.parse()` result before
   *  it's written to IDB. Returning `undefined` skips migrating that key
   *  (parse succeeded but the shape was garbage) without throwing and
   *  without touching its legacy localStorage key. Omit to use the parsed
   *  value as-is. */
  reviveLegacy?: (idbKey: string, parsed: unknown) => T | undefined;
}

export interface IdbKvStore<T> {
  /** Idempotent (internally memoized). Migrates any present legacy
   *  localStorage keys into IDB — independently per key, so one corrupt
   *  legacy value never blocks migrating the others — then hydrates the
   *  in-memory cache from IDB. Resolves even when IDB is unavailable,
   *  falling back to a read-only cache sourced from the legacy localStorage
   *  keys (console.warn'd), matching the existing draft-store/
   *  published-snapshot behavior. */
  init(): Promise<void>;
  /** Synchronous; reads the in-memory cache hydrated by `init()`. */
  read(key: string): T | undefined;
  /** Synchronous snapshot of every cached entry. */
  readAll(): Map<string, T>;
  /** Updates the cache immediately; the returned promise resolves once IDB
   *  acknowledges the write — reject means storage is full or unavailable. */
  write(key: string, value: T): Promise<void>;
  /** Removes a key. Cache updated immediately, IDB delete queued. */
  delete(key: string): Promise<void>;
  /** Generalizes the guest-to-signed-in-user migration-on-login pattern:
   *  copies the value at `fromKey` to `toKey` only if `toKey` has no
   *  existing value, then deletes `fromKey`. No-op if `fromKey` is empty or
   *  `toKey` is already populated. */
  rekey(fromKey: string, toKey: string): Promise<void>;
  /** One-off legacy migration for a key whose *name* isn't known until
   *  runtime (e.g. a per-user localStorage key like `cnt-quick-replies-
   *  {email}`, discovered only once that user's email is known — unlike
   *  `legacyKeys` in the config, which only covers keys fixed at store
   *  construction time). No-op if `idbKey` already has a value (never
   *  overwrites real data) or if `legacyKey` has none. Safe to call
   *  repeatedly — a successful migration removes `legacyKey`, so the next
   *  call for the same pair sees nothing left to do. */
  migrateLegacyKey(idbKey: string, legacyKey: string): Promise<void>;
  /** Test-only: clears the in-memory cache and init state so specs get a
   *  clean slate between cases. Production code never calls this. */
  __resetForTesting(): void;
}

function isBrowser(): boolean {
  return typeof indexedDB !== 'undefined' && typeof window !== 'undefined';
}

function reqDone<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb request failed'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('idb transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('idb transaction aborted'));
  });
}

// One shared open-connection promise per database name, so the ~20+ stores
// that default to 'cnt-landing-kv' don't each open a redundant connection.
const dbConnections = new Map<string, Promise<IDBDatabase>>();

function openSharedDb(dbName: string, storeName: string, version: number): Promise<IDBDatabase> {
  const cached = dbConnections.get(dbName);
  if (cached) return cached;
  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(dbName, version);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(storeName)) db.createObjectStore(storeName);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
  dbConnections.set(dbName, promise);
  return promise;
}

/** Test-only: forgets memoized open-connection promises so specs that swap
 *  out the global `indexedDB` (e.g. via `fake-indexeddb`) between cases
 *  don't reuse a connection tied to the old one. */
export function __resetIdbConnectionsForTesting(): void {
  dbConnections.clear();
}

export function createIdbKvStore<T>(config: IdbKvStoreConfig<T>): IdbKvStore<T> {
  const dbName = config.dbName ?? DEFAULT_DB_NAME;
  const storeName = config.storeName ?? DEFAULT_STORE_NAME;
  const version = config.version ?? DEFAULT_VERSION;
  const legacyKeys = config.legacyKeys;
  const reviveLegacy = config.reviveLegacy;

  const cache = new Map<string, T>();
  let initPromise: Promise<void> | null = null;

  function openDb(): Promise<IDBDatabase> {
    return openSharedDb(dbName, storeName, version);
  }

  function reviveOne(idbKey: string, raw: string): T | undefined {
    const parsed = JSON.parse(raw) as unknown;
    return reviveLegacy ? reviveLegacy(idbKey, parsed) : (parsed as T);
  }

  /** Migrates each legacy key independently — a corrupt value for one key
   *  is skipped (legacy key left in place) without blocking the others. */
  async function migrateLegacyKeys(db: IDBDatabase): Promise<void> {
    const toMigrate: Array<{ idbKey: string; legacyKey: string; value: T }> = [];
    for (const [idbKey, legacyKey] of Object.entries(legacyKeys)) {
      try {
        const raw = localStorage.getItem(legacyKey);
        if (raw == null) continue;
        const value = reviveOne(idbKey, raw);
        if (value === undefined) continue;
        toMigrate.push({ idbKey, legacyKey, value });
      } catch {
        // Corrupt legacy value for this key — skip it, leave it in place.
      }
    }
    if (toMigrate.length === 0) return;
    const tx = db.transaction(storeName, 'readwrite');
    for (const { idbKey, value } of toMigrate) tx.objectStore(storeName).put(value, idbKey);
    await txDone(tx);
    for (const { legacyKey } of toMigrate) localStorage.removeItem(legacyKey);
  }

  async function hydrateCache(db: IDBDatabase): Promise<void> {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const [keys, values] = await Promise.all([reqDone(store.getAllKeys()), reqDone(store.getAll())]);
    const klist = keys as IDBValidKey[];
    const vlist = values as T[];
    cache.clear();
    for (let i = 0; i < klist.length; i++) cache.set(String(klist[i]), vlist[i]);
  }

  function fallbackToLocalStorage(): void {
    for (const [idbKey, legacyKey] of Object.entries(legacyKeys)) {
      try {
        const raw = localStorage.getItem(legacyKey);
        if (raw == null) continue;
        const value = reviveOne(idbKey, raw);
        if (value !== undefined) cache.set(idbKey, value);
      } catch {
        // Corrupt legacy data — leave this key out of the fallback view.
      }
    }
  }

  function init(): Promise<void> {
    if (!isBrowser()) return Promise.resolve();
    if (initPromise) return initPromise;
    initPromise = (async () => {
      try {
        const db = await openDb();
        try {
          await migrateLegacyKeys(db);
        } catch {
          // Migration transaction itself failed (e.g. quota) — IDB hydrate below still runs.
        }
        await hydrateCache(db);
      } catch (err) {
        console.warn(`[idb-kv-store:${dbName}] IDB unavailable, falling back to localStorage`, err);
        cache.clear();
        fallbackToLocalStorage();
      }
    })();
    return initPromise;
  }

  return {
    init,

    read(key: string): T | undefined {
      return cache.get(key);
    },

    readAll(): Map<string, T> {
      return new Map(cache);
    },

    async write(key: string, value: T): Promise<void> {
      cache.set(key, value);
      if (!isBrowser()) return;
      const db = await openDb();
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).put(value, key);
      await txDone(tx);
    },

    async delete(key: string): Promise<void> {
      cache.delete(key);
      if (!isBrowser()) return;
      const db = await openDb();
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).delete(key);
      await txDone(tx);
    },

    async rekey(fromKey: string, toKey: string): Promise<void> {
      const value = cache.get(fromKey);
      if (value === undefined || cache.has(toKey)) return;
      cache.set(toKey, value);
      cache.delete(fromKey);
      if (!isBrowser()) return;
      const db = await openDb();
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).put(value, toKey);
      tx.objectStore(storeName).delete(fromKey);
      await txDone(tx);
    },

    async migrateLegacyKey(idbKey: string, legacyKey: string): Promise<void> {
      if (cache.has(idbKey)) return;
      let raw: string | null;
      try { raw = localStorage.getItem(legacyKey); } catch { return; }
      if (raw == null) return;
      let value: T | undefined;
      try { value = reviveOne(idbKey, raw); } catch { return; }
      if (value === undefined) return;
      cache.set(idbKey, value);
      if (!isBrowser()) return;
      try {
        const db = await openDb();
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).put(value, idbKey);
        await txDone(tx);
        localStorage.removeItem(legacyKey);
      } catch {
        // IDB write failed — cache still reflects the migrated value for
        // this session (best-effort, same contract as write()); legacy key
        // stays in place so a later successful write() attempt can retry.
      }
    },

    __resetForTesting(): void {
      cache.clear();
      initPromise = null;
    },
  };
}
